import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as Arrow } from '../../../../images/icons/fs-lc-arrow.svg';
import { PrimaryLink } from '../../../Common/Links';
import SmartLink from '../../../Common/SmartLink';
import ListItem from './ListItem';
import * as styles from './MobileMenu.module.scss';

const SLIDING_DURATION = 300; // ms

const HamburgerIcon = ({ isOpen }) => (
  <svg
    className={[styles.hamburger, isOpen ? styles.open : styles.closed].join(' ')}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path className={styles.top} strokeWidth="2" d="M27 2H-3"></path>
    <path className={styles.middle} strokeWidth="2" d="M27 12H-3"></path>
    <path className={styles.bottom} strokeWidth="2" d="M27 22H-3"></path>
  </svg>
);

export default function MobileMenu({ menuItems }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState([]);
  const resetTimeoutRef = useRef(null);
  const menuListsRef = useRef(null);
  const demoButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(styles.menuOpen);
      window.scrollTo(0, 0);
    } else {
      document.body.classList.remove(styles.menuOpen);
    }
  }, [isOpen]);

  const showSubmenu = (items) => {
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    setActiveMenu(items || []);
    if (menuListsRef.current) menuListsRef.current.style = `transform: translateX(calc(-100vw + var(--x-padding)));`;
    if (demoButtonRef.current) demoButtonRef.current.classList.remove(styles.fade);
  };

  const handleBack = () => {
    if (menuListsRef.current) menuListsRef.current.style = `transform: translateX(0)`;
    if (demoButtonRef.current) demoButtonRef.current.classList.add(styles.fade);
    resetTimeoutRef.current = setTimeout(() => setActiveMenu([]), SLIDING_DURATION);
  };

  return (
    <div className={styles.mobileMenu} data-analytics-component={'mobile-menu'}>
      <button className={styles.menuButton} onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        <HamburgerIcon isOpen={isOpen} />
      </button>
      <div
        className={[styles.menuContent, isOpen ? styles.visible : styles.hidden].join(' ')}
        aria-hidden={!isOpen}
        style={{ '--transitionDuration': `${SLIDING_DURATION}ms` }}
      >
        <div className={styles.menuLists} ref={menuListsRef}>
          <ul className={styles.list}>
            {menuItems.map((item, i) => (
              <li
                data-analytics-component={'mobile-menu-item'}
                data-analytics-id={item.title}
                className={styles.primaryItem}
                key={`${item.title}-${i}`}
              >
                {item.url ? (
                  <SmartLink to={item.url} data-analytics-id={item.title}>
                    {item.title}
                  </SmartLink>
                ) : (
                  <button data-analytics-id={item.title} onClick={() => showSubmenu(item)}>
                    {item.title}
                  </button>
                )}
              </li>
            ))}
            <li data-analytics-component={'mobile-menu-item'} data-analytics-id="log-in" className={styles.primaryItem}>
              <SmartLink to="/ui/">Log in</SmartLink>
            </li>
          </ul>
          <div className={styles.list}>
            <button className={styles.backButton} onClick={handleBack}>
              <Arrow /> Back
            </button>
            {activeMenu.subItems?.map((subItem, j) => (
              <ul
                key={`${subItem.title}-${j}`}
                className={[styles.submenuList, subItem.colorTheme ? styles[subItem.colorTheme] : ''].join(' ')}
              >
                <ListItem styles={styles} item={subItem} isTitle={true} />
                {subItem.subItems &&
                  subItem.subItems.map((sub, k) => <ListItem styles={styles} item={sub} key={`${sub.title}-${k}`} />)}
                {subItem.cta && <ListItem styles={styles} item={subItem.cta} isCta={true} />}
              </ul>
            ))}
            {activeMenu.callouts?.map((callout, c) => (
              <SmartLink
                key={`callout-${c}`}
                to={callout.url}
                className={[styles.callout, callout.colorTheme ? styles[callout.colorTheme] : ''].join(' ')}
                data-analytics-component={'mobile-menu-callout'}
                data-analytics-id={callout.title}
              >
                {callout.imageSrc && <img loading="lazy" src={callout.imageSrc} alt={callout.title} />}
                <div className={styles.calloutTitle}>
                  <span>{callout.title}</span> <Arrow className={styles.arrow} />
                </div>
                <div className={styles.calloutDescription}>{callout.description}</div>
              </SmartLink>
            ))}
          </div>
        </div>
        <div ref={demoButtonRef} className={styles.demoButton}>
          <PrimaryLink isButton={true} url="/demo/" label="Get a demo" />
        </div>
      </div>
    </div>
  );
}
